import httpNoAuthClient from '@common/api/httpNoAuthClient';
import {
  type CreatePendingDeviceRequestBase,
  type CreatePendingDeviceResponse,
  type PendingDeviceRegistration,
} from '@common/types/api';
import { type GUID } from '@common/types/brandedTypes';
import { type DeviceRegistrationFlowType } from '@common/types/store';
import { extractKeyText, FilterName } from '@common/utils/device/deviceFilter';

const gatewayPath = (type: DeviceRegistrationFlowType) => (type === 'isg' ? 'Isg' : 'Wss');

const tidyRequest = (registration: PendingDeviceRegistration): CreatePendingDeviceRequestBase => ({
  firstName: registration.firstName.trim(),
  lastName: registration.lastName.trim(),
  emailAddress: registration.emailAddress.trim(),
  street: registration.street.trim(),
  houseNumber: registration.houseNumber.trim(),
  postalCode: registration.postalCode.trim(),
  city: registration.city.trim(),
  // The dropdown stores a prefixed filter value (country-DE); the API wants the ISO code.
  countryCode: extractKeyText(registration.countryCode, FilterName.country),
  phone: registration.phone.trim() || undefined,
});

/**
 * Registers a device ('isg' → ISG gateway by MAC, otherwise WSS by PID). Trims every field,
 * turns the dropdown's prefixed country (country-DE) into its ISO code and drops an empty phone.
 * @throws {ApiError} `rate-limited` when too many registrations were made recently (429).
 */
export async function createPendingDevice(
  type: DeviceRegistrationFlowType,
  registration: PendingDeviceRegistration,
): Promise<GUID> {
  const identifier = registration.deviceIdentifier.trim();
  const { data } = await httpNoAuthClient.post<CreatePendingDeviceResponse>(
    `/rest/v1/PendingDevices/${gatewayPath(type)}`,
    {
      ...tidyRequest(registration),
      ...(type === 'isg' ? { mac: identifier } : { pid: identifier }),
    },
  );
  return data.id;
}

/** @throws {ApiError} `rate-limited` when the portal key was re-sent too often recently (429). */
export async function resendPortalKeyEmail(
  type: DeviceRegistrationFlowType,
  registrationId: GUID,
): Promise<void> {
  await httpNoAuthClient
    .post(`/rest/v1/PendingDevices/${gatewayPath(type)}/${registrationId}/PortalKeyEmail`);
}
